import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Link,
  useNavigate,
  useParams,
} from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useAuth } from "../context/AuthContext";

const FALLBACK_COVER =
  "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=1600&auto=format&fit=crop";

const ACTIVITIES = [
  ["hiking", "Planinarenje"],
  ["trekking", "Trekking"],
  ["camping", "Kampovanje"],
  ["cycling", "Biciklizam"],
  ["mountain biking", "MTB"],
  ["trail running", "Trail running"],
  ["climbing", "Penjanje"],
  ["via ferrata", "Via ferrata"],
  ["caving", "Speleologija"],
  ["canyoning", "Kanjoning"],
  ["rafting", "Rafting"],
  ["kayaking", "Kajak"],
  ["canoeing", "Kanu"],
  ["sup", "SUP"],
  ["sailing", "Jedrenje"],
  ["surfing", "Surfing"],
  ["kitesurfing", "Kitesurfing"],
  ["diving", "Ronjenje"],
  ["paragliding", "Paraglajding"],
  ["skydiving", "Padobranstvo"],
  ["skiing", "Skijanje"],
  ["snowboarding", "Snowboarding"],
  ["snowshoeing", "Krpljanje"],
  ["horse riding", "Jahanje"],
  ["fishing", "Ribolov"],
  ["off-road", "Off-road / 4x4"],
  ["nature trip", "Izlet u prirodi"],
  ["other", "Ostalo"],
];

const INCLUDED_PRESETS = [
  "Ručak",
  "Prevoz",
  "Vodič",
  "Oprema",
  "Noćenje",
  "Fotografisanje",
  "Osiguranje",
  "Piće / osveženje",
  "Ulaznice",
  "Kupanje",
];

function Icon({
  name,
  size = 20,
  strokeWidth = 2,
}) {
  const icons = {
    edit: (
      <>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" />
      </>
    ),
    mapPin: (
      <>
        <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
        <circle cx="12" cy="10" r="2.5" />
      </>
    ),
    users: (
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3 20v-2a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v2" />
        <path d="M16 4.5a3 3 0 0 1 0 6M17 13a5 5 0 0 1 4 5v2" />
      </>
    ),
    euro: (
      <>
        <path d="M18 7.5A6 6 0 1 0 18 16.5" />
        <path d="M5 10h8M5 14h8" />
      </>
    ),
    calendar: (
      <>
        <rect
          x="3"
          y="5"
          width="18"
          height="16"
          rx="2"
        />
        <path d="M16 3v4M8 3v4M3 10h18" />
      </>
    ),
    image: (
      <>
        <rect
          x="3"
          y="4"
          width="18"
          height="16"
          rx="2"
        />
        <circle cx="9" cy="9" r="2" />
        <path d="m21 15-5-5L5 20" />
      </>
    ),
    upload: (
      <>
        <path d="M12 16V4" />
        <path d="m7 9 5-5 5 5" />
        <path d="M5 20h14" />
      </>
    ),
    check: <path d="m5 12 4 4L19 6" />,
    alert: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v5M12 16h.01" />
      </>
    ),
    shield: (
      <>
        <path d="M12 3 5 6v5c0 4.6 2.9 8.4 7 10 4.1-1.6 7-5.4 7-10V6l-7-3Z" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),
    arrowRight: (
      <>
        <path d="M5 12h14" />
        <path d="m13 6 6 6-6 6" />
      </>
    ),
    clock: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    refresh: (
      <>
        <path d="M20 7v5h-5M4 17v-5h5" />
        <path d="M18.4 9A7 7 0 0 0 6 6.5L4 9M5.6 15A7 7 0 0 0 18 17.5l2-2.5" />
      </>
    ),
    trash: (
      <>
        <path d="M3 6h18" />
        <path d="M8 6V4h8v2" />
        <path d="m19 6-1 15H6L5 6" />
      </>
    ),
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {icons[name]}
    </svg>
  );
}

function toLocalInputValue(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const offset =
    date.getTimezoneOffset() * 60000;

  return new Date(
    date.getTime() - offset
  )
    .toISOString()
    .slice(0, 16);
}

function toIsoValue(value) {
  if (!value) return null;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
}

function formatPreviewDate(value) {
  if (!value) return "Termin po dogovoru";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Termin po dogovoru";
  }

  return new Intl.DateTimeFormat(
    "sr-Latn-RS",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(date);
}

function getFileExtension(file) {
  const fromName = file?.name
    ?.split(".")
    .pop()
    ?.toLowerCase();

  if (fromName) {
    return fromName;
  }

  const fromType = file?.type
    ?.split("/")
    .pop()
    ?.toLowerCase();

  return fromType || "jpg";
}

function LoadingState() {
  return (
    <>
      <EditEventStyles />

      <main className="editEventStatePage">
        <div className="editEventStateCard">
          <span className="editEventLoader" />

          <h1>Učitavanje avanture</h1>

          <p>
            Pripremamo podatke za
            uređivanje.
          </p>
        </div>
      </main>
    </>
  );
}

export default function EditEvent() {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    profile,
    isHost,
    loading: authLoading,
  } = useAuth();

  const [event, setEvent] =
    useState(null);
  const [pageLoading, setPageLoading] =
    useState(true);
  const [saving, setSaving] =
    useState(false);
  const [error, setError] =
    useState("");
  const [activities, setActivities] = useState([]);
  const [includedItems, setIncludedItems] = useState([]);
  const [customIncludedItem, setCustomIncludedItem] = useState("");
  const [newCoverIndex, setNewCoverIndex] = useState(null);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [newPhotoFiles, setNewPhotoFiles] = useState([]);
  const [coverUrl, setCoverUrl] = useState("");

  const [form, setForm] = useState({
    title: "",
    description: "",
    location: "",
    country: "",
    price: "",
    min_participants: "",
    capacity: "",
    start_date: "",
    end_date: "",
  });

  const loadEvent = useCallback(async () => {
    if (authLoading) return;

    if (!profile?.id || !id) {
      setEvent(null);
      setPageLoading(false);
      return;
    }

    setPageLoading(true);
    setError("");

    try {
      const {
        data,
        error: loadError,
      } = await supabase
        .from("events")
        .select("*")
        .eq("id", id)
        .single();

      if (loadError) {
        throw loadError;
      }

      if (!data) {
        throw new Error(
          "Avantura nije pronađena."
        );
      }

      if (data.host_id !== profile.id) {
        throw new Error(
          "Možeš da uređuješ samo svoje avanture."
        );
      }

      setEvent(data);
      setActivities(Array.isArray(data.activities) ? data.activities : []);
      setIncludedItems(Array.isArray(data.included_items) ? data.included_items : []);
      setCustomIncludedItem("");
      setNewCoverIndex(null);

      const initialPhotos = Array.from(
        new Set([data.cover_url, ...(Array.isArray(data.gallery_urls) ? data.gallery_urls : [])].filter(Boolean))
      ).slice(0, 8);
      setExistingPhotos(initialPhotos);
      setNewPhotoFiles([]);
      setCoverUrl(data.cover_url || initialPhotos[0] || "");

      setForm({
        title: data.title || "",
        description:
          data.description || "",
        location:
          data.location || "",
        country:
          data.country || "",
        price:
          data.price === null ||
          data.price === undefined
            ? ""
            : String(data.price),
        min_participants:
          data.min_participants === null ||
          data.min_participants === undefined
            ? "1"
            : String(data.min_participants),
        capacity:
          data.capacity === null ||
          data.capacity === undefined
            ? ""
            : String(data.capacity),
        start_date: toLocalInputValue(
          data.start_date
        ),
        end_date: toLocalInputValue(
          data.end_date
        ),
      });
    } catch (loadError) {
      console.error(
        "Greška pri učitavanju avanture:",
        loadError
      );

      setEvent(null);
      setError(
        loadError?.message ||
          "Avanturu trenutno nije moguće učitati."
      );
    } finally {
      setPageLoading(false);
    }
  }, [
    authLoading,
    id,
    profile?.id,
  ]);

  useEffect(() => {
    loadEvent();
  }, [loadEvent]);

  function updateField(name, value) {
    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (error) {
      setError("");
    }
  }

  const previewLocation = useMemo(
    () =>
      [form.location, form.country]
        .filter(Boolean)
        .join(", ") ||
      "Lokacija nije navedena",
    [form.location, form.country]
  );

  const newPhotoPreviews = useMemo(
    () => newPhotoFiles.map((file) => URL.createObjectURL(file)),
    [newPhotoFiles]
  );

  useEffect(() => {
    return () => {
      newPhotoPreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [newPhotoPreviews]);

  const previewCover =
    (newCoverIndex !== null ? newPhotoPreviews[newCoverIndex] : null) ||
    coverUrl ||
    existingPhotos[0] ||
    newPhotoPreviews[0] ||
    event?.cover_url ||
    FALLBACK_COVER;

  function toggleActivity(value) {
    setActivities((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
    );
    if (error) setError("");
  }

  function toggleIncludedItem(item) {
    setIncludedItems((current) =>
      current.includes(item)
        ? current.filter((value) => value !== item)
        : [...current, item]
    );
    if (error) setError("");
  }

  function addCustomIncludedItem() {
    const clean = customIncludedItem.trim();
    if (!clean) return;

    if (!includedItems.some((item) => item.toLowerCase() === clean.toLowerCase())) {
      setIncludedItems((current) => [...current, clean]);
    }

    setCustomIncludedItem("");
    if (error) setError("");
  }

  function removeIncludedItem(item) {
    setIncludedItems((current) => current.filter((value) => value !== item));
  }

  function handleNewPhotos(changeEvent) {
    const selected = Array.from(changeEvent.target.files || []);
    changeEvent.target.value = "";
    if (!selected.length) return;

    const invalid = selected.find((file) => !file.type.startsWith("image/"));
    if (invalid) {
      setError("Možeš dodati samo fotografije.");
      return;
    }

    const tooLarge = selected.find((file) => file.size > 8 * 1024 * 1024);
    if (tooLarge) {
      setError("Svaka fotografija može imati najviše 8 MB.");
      return;
    }

    const freeSlots = Math.max(8 - existingPhotos.length - newPhotoFiles.length, 0);
    if (!freeSlots) {
      setError("Možeš imati najviše 8 fotografija.");
      return;
    }

    if (selected.length > freeSlots) {
      setError(`Dodato je prvih ${freeSlots} fotografija. Maksimum je 8.`);
    }

    setNewPhotoFiles((current) => [...current, ...selected.slice(0, freeSlots)]);
  }

  function removeExistingPhoto(url) {
    setExistingPhotos((current) => current.filter((item) => item !== url));
    if (coverUrl === url) {
      const next = existingPhotos.find((item) => item !== url) || "";
      setCoverUrl(next);
      if (!next && newPhotoFiles.length) {
        setNewCoverIndex(0);
      }
    }
  }

  function removeNewPhoto(index) {
    setNewPhotoFiles((current) => current.filter((_, itemIndex) => itemIndex !== index));
    setNewCoverIndex((current) => {
      if (current === null) return null;
      if (index < current) return current - 1;
      if (index === current) return null;
      return current;
    });
  }

  function storagePathFromPublicUrl(url) {
    const marker = "/storage/v1/object/public/event-covers/";
    const index = url?.indexOf(marker);
    return index >= 0 ? decodeURIComponent(url.slice(index + marker.length)) : null;
  }

  async function uploadNewPhotos() {
    const uploaded = [];

    for (let index = 0; index < newPhotoFiles.length; index += 1) {
      const file = newPhotoFiles[index];
      const extension = getFileExtension(file);
      const filePath = [
        profile.id,
        event.id,
        `${Date.now()}-${index}-${Math.random().toString(36).slice(2)}.${extension}`,
      ].join("/");

      const { error: uploadError } = await supabase.storage
        .from("event-covers")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
          contentType: file.type || undefined,
        });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from("event-covers")
        .getPublicUrl(filePath);

      if (!data?.publicUrl) {
        throw new Error("Nije moguće dobiti URL nove fotografije.");
      }

      uploaded.push({ path: filePath, url: data.publicUrl });
    }

    return uploaded;
  }

  async function handleSave(eventSubmit) {
    eventSubmit.preventDefault();

    if (
      !event?.id ||
      !profile?.id ||
      saving
    ) {
      return;
    }

    try {
      setSaving(true);
      setError("");

      const title =
        form.title.trim();

      if (!activities.length) {
        throw new Error("Izaberi najmanje jednu aktivnost.");
      }

      if (!title) {
        throw new Error(
          "Naziv avanture je obavezan."
        );
      }

      const price = Number(
        form.price || 0
      );
      const minParticipants = Number(
        form.min_participants || 1
      );
      const capacity = Number(
        form.capacity || 1
      );

      if (
        Number.isNaN(price) ||
        price < 0
      ) {
        throw new Error(
          "Cena mora biti pozitivan broj."
        );
      }

      if (
        !Number.isInteger(minParticipants) ||
        minParticipants < 1
      ) {
        throw new Error(
          "Minimalan broj osoba mora biti najmanje 1."
        );
      }

      if (
        !Number.isInteger(capacity) ||
        capacity < 1
      ) {
        throw new Error(
          "Maksimalan broj osoba mora biti najmanje 1."
        );
      }

      if (minParticipants > capacity) {
        throw new Error(
          "Minimalan broj osoba ne može biti veći od maksimalnog."
        );
      }

      const startDate = toIsoValue(
        form.start_date
      );
      const endDate = toIsoValue(
        form.end_date
      );

      if (
        startDate &&
        endDate &&
        new Date(endDate).getTime() <
          new Date(startDate).getTime()
      ) {
        throw new Error(
          "Datum završetka ne može biti pre datuma početka."
        );
      }

      const uploaded = await uploadNewPhotos();
      const uploadedUrls = uploaded.map((item) => item.url);
      const allPhotoUrls = [...existingPhotos, ...uploadedUrls].slice(0, 8);

      let nextCoverUrl =
        newCoverIndex !== null
          ? uploadedUrls[newCoverIndex] || null
          : coverUrl;

      if (!nextCoverUrl || !allPhotoUrls.includes(nextCoverUrl)) {
        nextCoverUrl = allPhotoUrls[0] || null;
      }

      const galleryUrls = allPhotoUrls
        .filter((url) => url !== nextCoverUrl)
        .slice(0, 7);

      const {
        data: updatedEvent,
        error: updateError,
      } = await supabase
        .from("events")
        .update({
          activities,
          included_items: includedItems,
          title,
          description:
            form.description.trim(),
          location:
            form.location.trim(),
          country:
            form.country.trim(),
          price,
          min_participants: minParticipants,
          capacity,
          start_date: startDate,
          end_date: endDate,
          cover_url: nextCoverUrl,
          gallery_urls: galleryUrls,
          updated_at:
            new Date().toISOString(),
        })
        .eq("id", event.id)
        .eq("host_id", profile.id)
        .select("id")
        .maybeSingle();

      if (updateError) {
        throw updateError;
      }

      if (!updatedEvent) {
        if (uploaded.length) {
          await supabase.storage
            .from("event-covers")
            .remove(uploaded.map((item) => item.path));
        }
        throw new Error(
          "Avantura nije ažurirana. Proveri dozvole i vlasništvo."
        );
      }

      const originalPhotos = Array.from(
        new Set([event.cover_url, ...(Array.isArray(event.gallery_urls) ? event.gallery_urls : [])].filter(Boolean))
      );
      const removedPaths = originalPhotos
        .filter((url) => ![nextCoverUrl, ...galleryUrls].includes(url))
        .map(storagePathFromPublicUrl)
        .filter(Boolean);

      if (removedPaths.length) {
        const { error: removeError } = await supabase.storage
          .from("event-covers")
          .remove(removedPaths);
        if (removeError) {
          console.error("Stare fotografije nisu obrisane iz Storage-a:", removeError);
        }
      }

      navigate(`/event/${event.id}`);
    } catch (saveError) {
      console.error(
        "Greška pri čuvanju avanture:",
        saveError
      );

      setError(
        saveError?.message ||
          "Avanturu nije moguće ažurirati."
      );
    } finally {
      setSaving(false);
    }
  }

  if (
    authLoading ||
    pageLoading
  ) {
    return <LoadingState />;
  }

  if (!isHost) {
    return (
      <>
        <EditEventStyles />

        <main className="editEventStatePage">
          <div className="editEventStateCard">
            <span className="editEventStateIcon">
              <Icon
                name="shield"
                size={28}
              />
            </span>

            <h1>
              Pristup je namenjen hostovima
            </h1>

            <p>
              Samo host profili mogu da
              uređuju avanture.
            </p>

            <Link
              to="/"
              className="editEventStatePrimary"
            >
              Nazad na početnu

              <Icon
                name="arrowRight"
                size={16}
              />
            </Link>
          </div>
        </main>
      </>
    );
  }

  if (!event) {
    return (
      <>
        <EditEventStyles />

        <main className="editEventStatePage">
          <div className="editEventStateCard">
            <span className="editEventStateIcon danger">
              <Icon
                name="alert"
                size={28}
              />
            </span>

            <h1>
              Avanturu nije moguće urediti
            </h1>

            <p>
              {error ||
                "Avantura ne postoji ili nemaš dozvolu da je uređuješ."}
            </p>

            <div className="editEventStateActions">
              <button
                type="button"
                onClick={loadEvent}
              >
                <Icon
                  name="refresh"
                  size={16}
                />
                Pokušaj ponovo
              </button>

              <Link to="/dashboard">
                Otvori dashboard

                <Icon
                  name="arrowRight"
                  size={16}
                />
              </Link>
            </div>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <EditEventStyles />

      <main className="editEventPage">
        <section className="editEventHero">
          <div className="editEventHeroCopy">
            <span className="editEventEyebrow">
              <span />
              Host uređivanje
            </span>

            <h1>
              Uredi avanturu.
              <br />
              Sačuvaj atmosferu.
            </h1>

            <p>
              Ažuriraj svoju ponudu, lokaciju,
              termine, cenu, broj osoba i
              fotografije koje korisnici vide
              u MeetOutdoors katalogu.
            </p>
          </div>

          <div className="editEventHeroStats">
            <article>
              <strong>
                {form.title ||
                  "Avantura"}
              </strong>
              <span>
                objavljena ponuda
              </span>
            </article>

            <article>
              <strong>
                €{form.price || 0}
              </strong>
              <span>
                trenutna cena
              </span>
            </article>

            <article>
              <strong>
                {form.min_participants || 1}–{form.capacity || 1}
              </strong>
              <span>broj osoba</span>
            </article>
          </div>
        </section>

        <section className="editEventContent">
          <div className="editEventToolbar">
            <div>
              <span className="editEventSectionLabel">
                Uredi avanturu
              </span>

              <h2>
                Detalji koji vode
                iskustvo.
              </h2>

              <p>
                Izmene se čuvaju direktno
                u postojećoj ponudi.
              </p>
            </div>

            <Link
              to={`/event/${id}`}
            >
              <Icon
                name="image"
                size={16}
              />
              Pogledaj avanturu
            </Link>
          </div>

          <div className="editEventLayout">
            <form
              onSubmit={handleSave}
              className="editEventForm"
            >
              <section className="editEventPanel">
                <div className="editEventPanelHeader">
                  <span className="editEventPanelIcon">
                    <Icon name="check" size={19} />
                  </span>
                  <div>
                    <small>Aktivnosti</small>
                    <h2>Izaberi jednu ili više.</h2>
                  </div>
                </div>

                <div className="editEventActivityGrid">
                  {ACTIVITIES.map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={activities.includes(value) ? "active" : ""}
                      aria-pressed={activities.includes(value)}
                      onClick={() => toggleActivity(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </section>

              <section className="editEventPanel">
                <div className="editEventPanelHeader">
                  <span className="editEventPanelIcon">
                    <Icon name="check" size={19} />
                  </span>
                  <div>
                    <small>Šta je uključeno? · opciono</small>
                    <h2>Istakni usluge i pogodnosti.</h2>
                    <p>Izaberi predloge ili dodaj svoju stavku.</p>
                  </div>
                </div>

                <div className="editIncludedPresetGrid">
                  {INCLUDED_PRESETS.map((item) => (
                    <button
                      key={item}
                      type="button"
                      className={includedItems.includes(item) ? "active" : ""}
                      aria-pressed={includedItems.includes(item)}
                      onClick={() => toggleIncludedItem(item)}
                    >
                      ✓ {item}
                    </button>
                  ))}
                </div>

                <div className="editIncludedCustomRow">
                  <input
                    type="text"
                    maxLength={80}
                    value={customIncludedItem}
                    onChange={(changeEvent) => setCustomIncludedItem(changeEvent.target.value)}
                    onKeyDown={(keyEvent) => {
                      if (keyEvent.key === "Enter") {
                        keyEvent.preventDefault();
                        addCustomIncludedItem();
                      }
                    }}
                    placeholder="Npr. Kupanje na vodopadu"
                  />
                  <button type="button" onClick={addCustomIncludedItem}>Dodaj</button>
                </div>

                {includedItems.length > 0 && (
                  <div className="editIncludedSelected">
                    {includedItems.map((item) => (
                      <span key={item}>
                        ✓ {item}
                        <button type="button" onClick={() => removeIncludedItem(item)}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </section>

              <section className="editEventPanel">
                <div className="editEventPanelHeader">
                  <span className="editEventPanelIcon">
                    <Icon
                      name="edit"
                      size={19}
                    />
                  </span>

                  <div>
                    <small>
                      Osnovne informacije
                    </small>

                    <h2>
                      Naziv i opis.
                    </h2>
                  </div>
                </div>

                <div className="editEventFields">
                  <label className="editEventField full">
                    <span>
                      Naziv avanture
                    </span>

                    <input
                      value={form.title}
                      onChange={(changeEvent) =>
                        updateField(
                          "title",
                          changeEvent
                            .target.value
                        )
                      }
                      required
                    />
                  </label>

                  <label className="editEventField full">
                    <span>Opis</span>

                    <textarea
                      value={
                        form.description
                      }
                      onChange={(changeEvent) =>
                        updateField(
                          "description",
                          changeEvent
                            .target.value
                        )
                      }
                    />
                  </label>
                </div>
              </section>

              <section className="editEventPanel">
                <div className="editEventPanelHeader">
                  <span className="editEventPanelIcon">
                    <Icon
                      name="mapPin"
                      size={19}
                    />
                  </span>

                  <div>
                    <small>
                      Lokacija i uslovi ponude
                    </small>

                    <h2>
                      Gde, po kojoj ceni i za koliko osoba.
                    </h2>
                  </div>
                </div>

                <div className="editEventFields">
                  <label className="editEventField">
                    <span>
                      Lokacija
                    </span>

                    <input
                      value={
                        form.location
                      }
                      onChange={(changeEvent) =>
                        updateField(
                          "location",
                          changeEvent
                            .target.value
                        )
                      }
                    />
                  </label>

                  <label className="editEventField">
                    <span>Država</span>

                    <input
                      value={
                        form.country
                      }
                      onChange={(changeEvent) =>
                        updateField(
                          "country",
                          changeEvent
                            .target.value
                        )
                      }
                    />
                  </label>

                  <label className="editEventField">
                    <span>Cena</span>

                    <div className="editEventInputIcon">
                      <Icon
                        name="euro"
                        size={17}
                      />

                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={
                          form.price
                        }
                        onChange={(changeEvent) =>
                          updateField(
                            "price",
                            changeEvent
                              .target.value
                          )
                        }
                      />
                    </div>
                  </label>

                  <label className="editEventField">
                    <span>
                      Minimalan broj osoba
                    </span>

                    <div className="editEventInputIcon">
                      <Icon
                        name="users"
                        size={17}
                      />

                      <input
                        type="number"
                        min="1"
                        max={form.capacity || undefined}
                        step="1"
                        value={
                          form.min_participants
                        }
                        onChange={(changeEvent) =>
                          updateField(
                            "min_participants",
                            changeEvent.target.value
                          )
                        }
                        placeholder="6"
                        required
                      />
                    </div>
                  </label>

                  <label className="editEventField">
                    <span>
                      Maksimalan broj osoba
                    </span>

                    <div className="editEventInputIcon">
                      <Icon
                        name="users"
                        size={17}
                      />

                      <input
                        type="number"
                        min={form.min_participants || "1"}
                        step="1"
                        value={
                          form.capacity
                        }
                        onChange={(changeEvent) =>
                          updateField(
                            "capacity",
                            changeEvent.target.value
                          )
                        }
                        placeholder="15"
                        required
                      />
                    </div>
                  </label>
                </div>
              </section>

              <section className="editEventPanel">
                <div className="editEventPanelHeader">
                  <span className="editEventPanelIcon">
                    <Icon
                      name="calendar"
                      size={19}
                    />
                  </span>

                  <div>
                    <small>
                      Termin avanture · opciono
                    </small>

                    <h2>
                      Početak i završetak.
                    </h2>
                    <p>
                      Ostavi prazno ako je termin po dogovoru.
                    </p>
                  </div>
                </div>

                <div className="editEventFields">
                  <label className="editEventField">
                    <span>
                      Datum početka
                    </span>

                    <input
                      type="datetime-local"
                      value={
                        form.start_date
                      }
                      onChange={(changeEvent) =>
                        updateField(
                          "start_date",
                          changeEvent
                            .target.value
                        )
                      }
                    />
                  </label>

                  <label className="editEventField">
                    <span>
                      Datum završetka
                    </span>

                    <input
                      type="datetime-local"
                      value={
                        form.end_date
                      }
                      onChange={(changeEvent) =>
                        updateField(
                          "end_date",
                          changeEvent
                            .target.value
                        )
                      }
                    />
                  </label>
                </div>
              </section>

              <section className="editEventPanel">
                <div className="editEventPanelHeader">
                  <span className="editEventPanelIcon">
                    <Icon name="image" size={19} />
                  </span>
                  <div>
                    <small>Galerija avanture</small>
                    <h2>Do 8 fotografija.</h2>
                    <p>Na telefonu možeš izabrati više fotografija iz galerije.</p>
                  </div>
                </div>

                <label className="editEventUpload">
                  <span className="editEventUploadIcon">
                    <Icon name="upload" size={22} />
                  </span>
                  <div>
                    <strong>Dodaj fotografije</strong>
                    <p>{existingPhotos.length + newPhotoFiles.length} / 8 izabrano</p>
                  </div>
                  <span className="editEventUploadAction">Izaberi</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleNewPhotos}
                  />
                </label>

                {(existingPhotos.length > 0 || newPhotoFiles.length > 0) && (
                  <div className="editEventGalleryGrid">
                    {existingPhotos.map((url, index) => (
                      <article key={url} className={coverUrl === url ? "cover" : ""}>
                        <img src={url} alt={`Fotografija ${index + 1}`} />
                        {coverUrl === url && <span>Naslovna</span>}
                        <div>
                          {coverUrl !== url && (
                            <button
                              type="button"
                              onClick={() => {
                                setCoverUrl(url);
                                setNewCoverIndex(null);
                              }}
                            >
                              Naslovna
                            </button>
                          )}
                          <button type="button" onClick={() => removeExistingPhoto(url)}>
                            <Icon name="trash" size={14} />
                          </button>
                        </div>
                      </article>
                    ))}

                    {newPhotoPreviews.map((url, index) => (
                      <article
                        key={`new-${index}`}
                        className={newCoverIndex === index ? "cover" : ""}
                      >
                        <img src={url} alt={`Nova fotografija ${index + 1}`} />
                        <span>{newCoverIndex === index ? "Naslovna" : "Nova"}</span>
                        <div>
                          {newCoverIndex !== index && (
                            <button
                              type="button"
                              onClick={() => {
                                setNewCoverIndex(index);
                                setCoverUrl("");
                              }}
                            >
                              Naslovna
                            </button>
                          )}
                          <button type="button" onClick={() => removeNewPhoto(index)}>
                            <Icon name="trash" size={14} />
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>

              {error && (
                <div
                  className="editEventError"
                  role="alert"
                >
                  <span>
                    <Icon
                      name="alert"
                      size={18}
                    />
                  </span>

                  <p>{error}</p>
                </div>
              )}

              <div className="editEventSubmitBar">
                <div>
                  <span>
                    Spremno za čuvanje
                  </span>

                  <p>
                    Izmene će odmah biti
                    vidljive korisnicima
                    u katalogu.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={saving}
                >
                  <Icon
                    name="check"
                    size={17}
                  />

                  {saving
                    ? "Čuvanje..."
                    : "Sačuvaj izmene"}
                </button>
              </div>
            </form>

            <aside className="editEventPreview">
              <span className="editEventSectionLabel">
                Pregled avanture
              </span>

              <article className="editEventPreviewCard">
                <div className="editEventPreviewImage">
                  <img
                    src={previewCover}
                    alt={
                      form.title ||
                      "Pregled avanture"
                    }
                    onError={(imageEvent) => {
                      imageEvent.currentTarget.src =
                        FALLBACK_COVER;
                    }}
                  />

                  <span>
                    <Icon
                      name="edit"
                      size={14}
                    />
                    Live preview
                  </span>
                </div>

                <div className="editEventPreviewBody">
                  <small>
                    MeetOutdoors ponuda
                  </small>

                  <h2>
                    {form.title ||
                      "Naziv avanture"}
                  </h2>

                  <p className="editEventPreviewLocation">
                    <Icon
                      name="mapPin"
                      size={15}
                    />
                    {previewLocation}
                  </p>

                  <div className="editEventPreviewFacts">
                    <article>
                      <span>Cena</span>
                      <strong>
                        €{form.price || 0}
                      </strong>
                    </article>

                    <article>
                      <span>
                        Minimum
                      </span>
                      <strong>
                        {form.min_participants || 1}
                      </strong>
                    </article>

                    <article>
                      <span>
                        Maksimum
                      </span>
                      <strong>
                        {form.capacity || 1}
                      </strong>
                    </article>

                    <article>
                      <span>
                        Početak
                      </span>
                      <strong>
                        {formatPreviewDate(
                          form.start_date
                        )}
                      </strong>
                    </article>
                  </div>
                </div>
              </article>

              <div className="editEventPreviewHint">
                <Icon
                  name="clock"
                  size={17}
                />

                <p>
                  Pregled se automatski
                  menja dok uređuješ formu.
                </p>
              </div>
            </aside>
          </div>
        </section>
      </main>
    </>
  );
}

function EditEventStyles() {
  return (
    <style>{`
      * {
        box-sizing: border-box;
      }

      body {
        margin: 0;
        background: #edf1e9;
      }

      button,
      input,
      textarea {
        font: inherit;
      }

      button,
      a {
        -webkit-tap-highlight-color: transparent;
      }

      .editEventPage,
      .editEventStatePage {
        min-height: 100vh;
        color: #203229;
        font-family:
          Inter,
          ui-sans-serif,
          system-ui,
          -apple-system,
          BlinkMacSystemFont,
          "Segoe UI",
          sans-serif;
      }

      .editEventPage {
        padding: 118px 28px 70px;
        background:
          radial-gradient(
            circle at 7% 0%,
            rgba(177, 211, 139, 0.18),
            transparent 27%
          ),
          radial-gradient(
            circle at 94% 25%,
            rgba(64, 106, 75, 0.1),
            transparent 24%
          ),
          #edf1e9;
      }

      .editEventPage a {
        color: inherit;
        text-decoration: none;
      }

      .editEventHero {
        position: relative;
        isolation: isolate;
        width: min(1200px, 100%);
        min-height: 600px;
        margin: 0 auto;
        padding: 34px;
        overflow: hidden;
        border-radius: 36px;
        background:
          radial-gradient(
            circle at 84% 17%,
            rgba(202, 241, 148, 0.14),
            transparent 27%
          ),
          linear-gradient(
            135deg,
            #0d2a1a,
            #173f28 58%,
            #28563a
          );
        color: white;
        box-shadow:
          0 34px 90px
          rgba(23, 54, 36, 0.18);
      }

      .editEventHero::before {
        position: absolute;
        top: -170px;
        right: -140px;
        z-index: -1;
        width: 550px;
        height: 550px;
        border:
          1px solid
          rgba(255, 255, 255, 0.07);
        border-radius: 50%;
        content: "";
        box-shadow:
          0 0 0 80px
            rgba(255, 255, 255, 0.02),
          0 0 0 160px
            rgba(255, 255, 255, 0.012);
      }

      .editEventHeroCopy {
        max-width: 860px;
        padding-top: 105px;
      }

      .editEventEyebrow {
        display: inline-flex;
        align-items: center;
        gap: 9px;
        padding: 9px 13px;
        border:
          1px solid
          rgba(255, 255, 255, 0.14);
        border-radius: 999px;
        background:
          rgba(255, 255, 255, 0.07);
        color:
          rgba(255, 255, 255, 0.76);
        font-size: 9px;
        font-weight: 900;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        backdrop-filter: blur(13px);
      }

      .editEventEyebrow > span {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: #cef39a;
        box-shadow:
          0 0 0 5px
          rgba(206, 243, 154, 0.12);
      }

      .editEventHeroCopy h1 {
        margin: 24px 0 0;
        font-size:
          clamp(56px, 7.3vw, 94px);
        line-height: 0.9;
        letter-spacing: -0.075em;
      }

      .editEventHeroCopy p {
        max-width: 610px;
        margin: 25px 0 0;
        color:
          rgba(255, 255, 255, 0.63);
        font-size: 14px;
        line-height: 1.75;
      }

      .editEventHeroStats {
        position: absolute;
        right: 34px;
        bottom: 34px;
        left: 34px;
        display: grid;
        grid-template-columns:
          repeat(3, minmax(0, 1fr));
        gap: 12px;
      }

      .editEventHeroStats article {
        padding: 17px;
        border:
          1px solid
          rgba(255, 255, 255, 0.13);
        border-radius: 17px;
        background:
          rgba(12, 35, 21, 0.34);
        backdrop-filter: blur(16px);
      }

      .editEventHeroStats strong,
      .editEventHeroStats span {
        display: block;
      }

      .editEventHeroStats strong {
        overflow: hidden;
        font-size: 18px;
        letter-spacing: -0.03em;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .editEventHeroStats span {
        margin-top: 6px;
        color:
          rgba(255, 255, 255, 0.48);
        font-size: 8px;
        font-weight: 800;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }

      .editEventContent {
        width: min(1140px, 100%);
        margin: 0 auto;
      }

      .editEventToolbar {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 20px;
        margin: 50px 0 22px;
      }

      .editEventSectionLabel {
        color: #789456;
        font-size: 9px;
        font-weight: 900;
        letter-spacing: 0.13em;
        text-transform: uppercase;
      }

      .editEventToolbar h2 {
        margin: 8px 0 0;
        color: #2f4437;
        font-size:
          clamp(34px, 5vw, 52px);
        line-height: 1;
        letter-spacing: -0.06em;
      }

      .editEventToolbar p {
        margin: 10px 0 0;
        color: #7d8981;
        font-size: 10px;
      }

      .editEventToolbar > a {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        min-height: 43px;
        padding: 0 15px;
        border: 1px solid #d5dfd1;
        border-radius: 13px;
        background:
          rgba(255, 255, 255, 0.8);
        color: #4c6255;
        font-size: 9px;
        font-weight: 850;
      }

      .editEventLayout {
        display: grid;
        grid-template-columns:
          minmax(0, 1.35fr)
          minmax(300px, 0.65fr);
        align-items: start;
        gap: 18px;
      }

      .editEventForm {
        display: grid;
        gap: 18px;
      }

      .editEventPanel {
        padding: 24px;
        border: 1px solid #dbe4d8;
        border-radius: 26px;
        background:
          rgba(255, 255, 255, 0.78);
        box-shadow:
          0 14px 38px
          rgba(31, 51, 38, 0.05);
      }

      .editEventPanelHeader {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 20px;
      }

      .editEventPanelIcon {
        display: grid;
        place-items: center;
        flex: 0 0 auto;
        width: 45px;
        height: 45px;
        border-radius: 14px;
        background: #e7f0dc;
        color: #608047;
      }

      .editEventPanelHeader small {
        display: block;
        color: #789456;
        font-size: 8px;
        font-weight: 900;
        letter-spacing: 0.11em;
        text-transform: uppercase;
      }

      .editEventPanelHeader h2 {
        margin: 5px 0 0;
        color: #33483b;
        font-size: 23px;
        letter-spacing: -0.045em;
      }

      .editEventFields {
        display: grid;
        grid-template-columns:
          repeat(2, minmax(0, 1fr));
        gap: 14px;
      }

      .editEventField {
        display: grid;
        gap: 7px;
      }

      .editEventField.full {
        grid-column: 1 / -1;
      }

      .editEventField > span {
        color: #5f6f65;
        font-size: 9px;
        font-weight: 850;
      }

      .editEventField input,
      .editEventField textarea {
        width: 100%;
        border: 1px solid #dbe4d8;
        border-radius: 14px;
        background: #f8faf6;
        color: #33483b;
        outline: none;
        transition: 0.2s ease;
      }

      .editEventField input {
        min-height: 47px;
        padding: 0 13px;
      }

      .editEventField textarea {
        min-height: 125px;
        padding: 13px;
        line-height: 1.65;
        resize: vertical;
      }

      .editEventField input:focus,
      .editEventField textarea:focus {
        border-color: #9db28f;
        background: white;
        box-shadow:
          0 0 0 4px
          rgba(126, 158, 92, 0.1);
      }

      .editEventInputIcon {
        position: relative;
        color: #789456;
      }

      .editEventInputIcon > svg {
        position: absolute;
        top: 50%;
        left: 13px;
        z-index: 1;
        transform: translateY(-50%);
      }

      .editEventInputIcon input {
        padding-left: 40px;
      }

      .editEventUpload {
        position: relative;
        display: grid;
        grid-template-columns:
          auto minmax(0, 1fr) auto;
        align-items: center;
        gap: 13px;
        padding: 16px;
        overflow: hidden;
        border: 1px dashed #c7d4c3;
        border-radius: 17px;
        background: #f8faf6;
        cursor: pointer;
      }

      .editEventUploadIcon {
        display: grid;
        place-items: center;
        width: 46px;
        height: 46px;
        border-radius: 14px;
        background: #e7f0dc;
        color: #608047;
      }

      .editEventUpload strong,
      .editEventUpload p {
        display: block;
      }

      .editEventUpload strong {
        color: #3d5144;
        font-size: 10px;
      }

      .editEventUpload p {
        margin: 4px 0 0;
        color: #8b958e;
        font-size: 8px;
      }

      .editEventUploadAction {
        min-height: 36px;
        padding: 0 12px;
        border-radius: 11px;
        background: #183a27;
        color: white;
        font-size: 9px;
        font-weight: 850;
        line-height: 36px;
      }

      .editEventUpload input {
        position: absolute;
        width: 1px;
        height: 1px;
        opacity: 0;
        pointer-events: none;
      }

      .editEventRemoveCover {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-top: 10px;
        padding: 8px 10px;
        border: 0;
        border-radius: 10px;
        background: #fff0ee;
        color: #9a463c;
        cursor: pointer;
        font-size: 8px;
        font-weight: 800;
      }

      .editEventError {
        display: grid;
        grid-template-columns:
          auto minmax(0, 1fr);
        align-items: center;
        gap: 11px;
        padding: 14px;
        border: 1px solid #efc7c2;
        border-radius: 16px;
        background: #fff0ee;
        color: #963f35;
      }

      .editEventError > span {
        display: grid;
        place-items: center;
        width: 34px;
        height: 34px;
        border-radius: 10px;
        background: #f8d7d3;
      }

      .editEventError p {
        margin: 0;
        font-size: 10px;
      }

      .editEventSubmitBar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
        padding: 20px;
        border: 1px solid #cbd9c5;
        border-radius: 22px;
        background: #e9f1e2;
      }

      .editEventSubmitBar span {
        color: #52733f;
        font-size: 10px;
        font-weight: 850;
      }

      .editEventSubmitBar p {
        margin: 5px 0 0;
        color: #788674;
        font-size: 9px;
      }

      .editEventSubmitBar button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        flex: 0 0 auto;
        min-height: 46px;
        padding: 0 17px;
        border: 0;
        border-radius: 14px;
        background: #183a27;
        color: white;
        cursor: pointer;
        font-size: 10px;
        font-weight: 850;
        transition: 0.2s ease;
      }

      .editEventSubmitBar
        button:hover:not(:disabled) {
        gap: 11px;
        transform: translateY(-2px);
        background: #214b32;
      }

      .editEventSubmitBar
        button:disabled {
        cursor: wait;
        opacity: 0.62;
      }

      .editEventPreview {
        position: sticky;
        top: 108px;
      }

      .editEventPreviewCard {
        margin-top: 12px;
        overflow: hidden;
        border: 1px solid #dbe4d8;
        border-radius: 25px;
        background:
          rgba(255, 255, 255, 0.8);
        box-shadow:
          0 16px 42px
          rgba(31, 51, 38, 0.06);
      }

      .editEventPreviewImage {
        position: relative;
        height: 250px;
        overflow: hidden;
      }

      .editEventPreviewImage::after {
        position: absolute;
        inset: 0;
        background:
          linear-gradient(
            180deg,
            transparent 50%,
            rgba(11, 29, 18, 0.46)
          );
        content: "";
      }

      .editEventPreviewImage img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .editEventPreviewImage > span {
        position: absolute;
        top: 14px;
        left: 14px;
        z-index: 2;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        min-height: 31px;
        padding: 0 10px;
        border:
          1px solid
          rgba(255, 255, 255, 0.18);
        border-radius: 999px;
        background:
          rgba(17, 44, 27, 0.44);
        color: white;
        font-size: 8px;
        font-weight: 850;
        backdrop-filter: blur(12px);
      }

      .editEventPreviewBody {
        padding: 19px;
      }

      .editEventPreviewBody > small {
        color: #789456;
        font-size: 8px;
        font-weight: 900;
        letter-spacing: 0.1em;
        text-transform: uppercase;
      }

      .editEventPreviewBody h2 {
        margin: 8px 0 0;
        color: #304538;
        font-size: 25px;
        line-height: 1.05;
        letter-spacing: -0.05em;
      }

      .editEventPreviewLocation {
        display: flex;
        align-items: center;
        gap: 7px;
        margin: 10px 0 0;
        color: #768279;
        font-size: 9px;
      }

      .editEventPreviewFacts {
        display: grid;
        gap: 8px;
        margin-top: 16px;
      }

      .editEventPreviewFacts article {
        padding: 12px;
        border: 1px solid #e0e7dd;
        border-radius: 14px;
        background: #f8faf6;
      }

      .editEventPreviewFacts span,
      .editEventPreviewFacts strong {
        display: block;
      }

      .editEventPreviewFacts span {
        color: #8b958e;
        font-size: 8px;
      }

      .editEventPreviewFacts strong {
        margin-top: 4px;
        color: #405347;
        font-size: 10px;
        line-height: 1.4;
      }

      .editEventPreviewHint {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        margin-top: 12px;
        padding: 14px;
        border: 1px solid #d6e0d2;
        border-radius: 16px;
        background:
          rgba(255, 255, 255, 0.66);
        color: #608047;
      }

      .editEventPreviewHint p {
        margin: 0;
        color: #748174;
        font-size: 9px;
        line-height: 1.55;
      }

      .editEventStatePage {
        display: grid;
        place-items: center;
        padding: 118px 24px 24px;
        background:
          radial-gradient(
            circle at top left,
            rgba(166, 203, 126, 0.18),
            transparent 30%
          ),
          #edf1e9;
      }

      .editEventStateCard {
        display: grid;
        place-items: center;
        width: min(500px, 100%);
        padding: 50px 30px;
        border: 1px solid #dce3d9;
        border-radius: 28px;
        background:
          rgba(255, 255, 255, 0.84);
        text-align: center;
        box-shadow:
          0 20px 60px
          rgba(28, 48, 35, 0.08);
      }

      .editEventLoader {
        width: 38px;
        height: 38px;
        border: 3px solid #dce5d7;
        border-top-color: #52783c;
        border-radius: 50%;
        animation:
          editEventSpin
          0.8s linear infinite;
      }

      @keyframes editEventSpin {
        to {
          transform: rotate(360deg);
        }
      }

      .editEventStateIcon {
        display: grid;
        place-items: center;
        width: 64px;
        height: 64px;
        border-radius: 21px;
        background: #e7f0dc;
        color: #608047;
      }

      .editEventStateIcon.danger {
        background: #ffe9e5;
        color: #a85247;
      }

      .editEventStateCard h1 {
        margin: 18px 0 0;
        color: #263a2f;
        font-size: 28px;
        letter-spacing: -0.04em;
      }

      .editEventStateCard p {
        max-width: 390px;
        margin: 9px 0 0;
        color: #7e8981;
        font-size: 11px;
        line-height: 1.65;
      }

      .editEventStatePrimary,
      .editEventStateActions button,
      .editEventStateActions a {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        min-height: 42px;
        padding: 0 14px;
        border-radius: 12px;
        font-size: 10px;
        font-weight: 850;
        text-decoration: none;
      }

      .editEventStatePrimary {
        margin-top: 20px;
        background: #183a27;
        color: white !important;
      }

      .editEventStateActions {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 9px;
        margin-top: 20px;
      }

      .editEventStateActions button {
        border: 0;
        background: #183a27;
        color: white;
        cursor: pointer;
      }

      .editEventStateActions a {
        border: 1px solid #d5ded2;
        background: white;
        color: #51665a;
      }

      @media (max-width: 980px) {
        .editEventLayout {
          grid-template-columns: 1fr;
        }

        .editEventPreview {
          position: static;
        }

        .editEventHeroStats {
          grid-template-columns: 1fr;
        }

        .editEventHero {
          min-height: 720px;
        }
      }

      @media (max-width: 700px) {
        .editEventPage {
          padding: 84px 0 64px;
        }

        .editEventStatePage {
          padding-top: 84px;
        }

        .editEventHero {
          min-height: 750px;
          padding: 24px;
          border-radius:
            0 0 32px 32px;
        }

        .editEventHeroCopy {
          padding-top: 110px;
        }

        .editEventHeroStats {
          right: 24px;
          bottom: 24px;
          left: 24px;
        }

        .editEventContent {
          padding: 0 18px;
        }

        .editEventToolbar {
          align-items: flex-start;
          flex-direction: column;
        }

        .editEventFields {
          grid-template-columns: 1fr;
        }

        .editEventField.full {
          grid-column: auto;
        }

        .editEventSubmitBar {
          align-items: flex-start;
          flex-direction: column;
        }

        .editEventSubmitBar button {
          width: 100%;
        }
      }

      @media (max-width: 480px) {
        .editEventHero {
          min-height: 790px;
          padding: 19px;
        }

        .editEventHeroCopy h1 {
          font-size: 47px;
        }

        .editEventHeroStats {
          right: 19px;
          bottom: 19px;
          left: 19px;
        }

        .editEventContent {
          padding: 0 13px;
        }

        .editEventPanel {
          padding: 20px;
        }

        .editEventUpload {
          grid-template-columns:
            auto minmax(0, 1fr);
        }

        .editEventUploadAction {
          grid-column: 1 / -1;
          text-align: center;
        }
      }

      @media (
        prefers-reduced-motion: reduce
      ) {
        *,
        *::before,
        *::after {
          animation: none !important;
          scroll-behavior: auto !important;
          transition: none !important;
        }
      }

      /* ACTIVITIES */
      .editEventActivityGrid {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 8px;
        width: 100%;
        min-width: 0;
      }

      .editEventActivityGrid button {
        -webkit-appearance: none;
        appearance: none;
        width: 100%;
        min-width: 0;
        min-height: 40px;
        padding: 9px 10px;
        border: 1px solid rgba(36, 77, 54, 0.13);
        border-radius: 12px;
        background: #f8faf6;
        color: #33483b;
        cursor: pointer;
        font: inherit;
        font-size: 10px;
        font-weight: 800;
        line-height: 1.2;
        text-align: center;
        white-space: normal;
        overflow-wrap: anywhere;
        transition: 0.18s ease;
      }

      .editEventActivityGrid button:hover {
        border-color: #9db28f;
        background: #f1f6ed;
      }

      .editEventActivityGrid button.active {
        border-color: #244d36;
        background: #244d36;
        color: #fff;
      }

      /* INCLUDED ITEMS */
      .editIncludedPresetGrid {
        display: grid;
        grid-template-columns: repeat(5, minmax(0, 1fr));
        gap: 8px;
        width: 100%;
        min-width: 0;
      }

      .editIncludedPresetGrid button {
        -webkit-appearance: none;
        appearance: none;
        width: 100%;
        min-width: 0;
        min-height: 40px;
        padding: 9px 8px;
        border: 1px solid rgba(36, 77, 54, 0.13);
        border-radius: 12px;
        background: #f8faf6;
        color: #33483b;
        cursor: pointer;
        font: inherit;
        font-size: 9px;
        font-weight: 800;
        line-height: 1.2;
        text-align: center;
        white-space: normal;
        overflow-wrap: anywhere;
      }

      .editIncludedPresetGrid button.active {
        border-color: #244d36;
        background: #244d36;
        color: #fff;
      }

      .editIncludedCustomRow {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        gap: 8px;
        width: 100%;
        min-width: 0;
        margin-top: 11px;
      }

      .editIncludedCustomRow input {
        width: 100%;
        min-width: 0;
        min-height: 44px;
        padding: 0 13px;
        border: 1px solid #dbe4d8;
        border-radius: 13px;
        background: #f8faf6;
        color: #33483b;
        outline: none;
        font: inherit;
        font-size: 11px;
      }

      .editIncludedCustomRow button {
        -webkit-appearance: none;
        appearance: none;
        min-width: 82px;
        min-height: 44px;
        padding: 0 15px;
        border: 0;
        border-radius: 13px;
        background: #244d36;
        color: #fff;
        cursor: pointer;
        font: inherit;
        font-size: 10px;
        font-weight: 850;
      }

      .editIncludedSelected {
        display: flex;
        flex-wrap: wrap;
        gap: 7px;
        width: 100%;
        min-width: 0;
        margin-top: 11px;
      }

      .editIncludedSelected > span {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        max-width: 100%;
        padding: 7px 8px 7px 10px;
        border: 1px solid rgba(36, 77, 54, 0.09);
        border-radius: 999px;
        background: #edf4ef;
        color: #244d36;
        font-size: 9px;
        font-weight: 800;
        line-height: 1.2;
      }

      .editIncludedSelected > span > button {
        -webkit-appearance: none;
        appearance: none;
        display: grid;
        place-items: center;
        flex: 0 0 auto;
        width: 22px;
        min-width: 22px;
        height: 22px;
        min-height: 22px;
        margin: 0;
        padding: 0;
        border: 0;
        border-radius: 50%;
        background: rgba(36, 77, 54, 0.1);
        color: #244d36;
        cursor: pointer;
        font: inherit;
        font-size: 15px;
        line-height: 1;
      }

      /* GALLERY */
      .editEventGalleryGrid {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 10px;
        width: 100%;
        min-width: 0;
        margin-top: 13px;
      }

      .editEventGalleryGrid article {
        position: relative;
        min-width: 0;
        overflow: hidden;
        border: 1px solid rgba(36, 77, 54, 0.12);
        border-radius: 15px;
        background: #edf1e9;
        aspect-ratio: 4 / 3;
      }

      .editEventGalleryGrid article.cover {
        border-color: #244d36;
        box-shadow: inset 0 0 0 2px #244d36;
      }

      .editEventGalleryGrid img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .editEventGalleryGrid article > span {
        position: absolute;
        top: 8px;
        left: 8px;
        padding: 5px 8px;
        border-radius: 999px;
        background: rgba(13, 35, 23, 0.88);
        color: #fff;
        font-size: 8px;
        font-weight: 850;
      }

      .editEventGalleryGrid article > div {
        position: absolute;
        right: 7px;
        bottom: 7px;
        left: 7px;
        display: flex;
        justify-content: flex-end;
        gap: 6px;
      }

      .editEventGalleryGrid article > div button {
        -webkit-appearance: none;
        appearance: none;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 31px;
        padding: 0 9px;
        border: 0;
        border-radius: 9px;
        background: rgba(255, 255, 255, 0.94);
        color: #244d36;
        cursor: pointer;
        font: inherit;
        font-size: 8px;
        font-weight: 850;
      }

      @media (max-width: 900px) {
        .editEventActivityGrid,
        .editIncludedPresetGrid {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }

        .editEventGalleryGrid {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }
      }

      @media (max-width: 700px) {
        .editEventActivityGrid,
        .editIncludedPresetGrid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
        }

        .editEventActivityGrid button,
        .editIncludedPresetGrid button {
          min-height: 44px;
          padding: 9px 10px;
          font-size: 11px;
        }

        .editIncludedCustomRow {
          grid-template-columns: minmax(0, 1fr) 82px;
        }

        .editIncludedCustomRow button {
          width: 82px;
          min-width: 82px;
          padding: 0 8px;
          font-size: 11px;
        }

        .editIncludedSelected > span {
          font-size: 10px;
        }

        .editEventGalleryGrid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
        }
      }

      @media (max-width: 390px) {
        .editEventPanel {
          padding: 17px;
        }

        .editIncludedCustomRow {
          grid-template-columns: 1fr;
        }

        .editIncludedCustomRow button {
          width: 100%;
          min-width: 0;
        }
      }
    
      /* =========================================================
         MEETOUTDOORS — COMPACT PREMIUM EDITOR PASS
         Visual-only override. Existing data/save logic is untouched.
         ========================================================= */

      .editEventPage {
        padding: 92px 18px 48px;
        background:
          radial-gradient(circle at 12% 0%, rgba(186, 224, 145, .16), transparent 24%),
          radial-gradient(circle at 92% 18%, rgba(32, 83, 52, .08), transparent 25%),
          #f1f4ed;
      }

      .editEventHero {
        width: min(1120px, 100%);
        min-height: 315px;
        padding: 28px 30px;
        border-radius: 28px;
        box-shadow: 0 22px 58px rgba(19, 48, 31, .15);
      }

      .editEventHeroCopy {
        max-width: 690px;
        padding-top: 24px;
      }

      .editEventEyebrow {
        padding: 7px 11px;
        font-size: 8px;
      }

      .editEventHeroCopy h1 {
        margin-top: 18px;
        font-size: clamp(42px, 5.5vw, 68px);
        line-height: .94;
        letter-spacing: -.065em;
      }

      .editEventHeroCopy p {
        max-width: 570px;
        margin-top: 16px;
        font-size: 12px;
        line-height: 1.6;
      }

      .editEventHeroStats {
        right: 30px;
        bottom: 25px;
        left: auto;
        width: min(410px, 42%);
        gap: 8px;
      }

      .editEventHeroStats article {
        padding: 12px 13px;
        border-radius: 14px;
      }

      .editEventHeroStats strong { font-size: 14px; }
      .editEventHeroStats span { margin-top: 4px; font-size: 7px; }

      .editEventContent {
        width: min(1080px, 100%);
      }

      .editEventToolbar {
        align-items: center;
        margin: 30px 0 14px;
      }

      .editEventToolbar h2 {
        margin-top: 5px;
        font-size: clamp(28px, 4vw, 38px);
      }

      .editEventToolbar p { margin-top: 7px; }

      .editEventLayout {
        grid-template-columns: minmax(0, 1.5fr) minmax(270px, .62fr);
        gap: 14px;
      }

      .editEventForm { gap: 12px; }

      .editEventPanel {
        padding: 18px;
        border-radius: 20px;
        background: rgba(255,255,255,.88);
        box-shadow: 0 10px 28px rgba(31, 51, 38, .045);
      }

      .editEventPanelHeader {
        gap: 10px;
        margin-bottom: 14px;
      }

      .editEventPanelIcon {
        width: 38px;
        height: 38px;
        border-radius: 12px;
      }

      .editEventPanelHeader h2 {
        margin-top: 3px;
        font-size: 18px;
      }

      .editEventFields { gap: 10px; }
      .editEventField { gap: 5px; }

      .editEventField input {
        min-height: 43px;
        padding: 0 12px;
      }

      .editEventField textarea {
        min-height: 96px;
        padding: 11px 12px;
      }

      .editEventField input,
      .editEventField textarea {
        border-radius: 12px;
        background: #fbfcf9;
      }

      .editEventActivityGrid,
      .editIncludedPresetGrid {
        gap: 7px;
      }

      .editEventActivityGrid button,
      .editIncludedPresetGrid button {
        min-height: 36px;
        padding: 7px 10px;
        border-radius: 11px;
      }

      .editIncludedCustomRow { margin-top: 10px; }

      .editEventUpload {
        padding: 12px;
        gap: 10px;
        border-radius: 14px;
      }

      .editEventUploadIcon {
        width: 40px;
        height: 40px;
        border-radius: 12px;
      }

      .editEventGalleryGrid { gap: 8px; }

      .editEventSubmitBar {
        padding: 13px 15px;
        border-radius: 17px;
        box-shadow: 0 12px 30px rgba(24,58,39,.10);
      }

      .editEventSubmitBar button {
        min-height: 43px;
        border-radius: 12px;
      }

      .editEventPreview {
        top: 90px;
      }

      .editEventPreviewCard {
        border-radius: 20px;
        box-shadow: 0 14px 34px rgba(31,51,38,.07);
      }

      .editEventPreviewImage {
        aspect-ratio: 1.45 / 1;
      }

      .editEventPreviewBody { padding: 16px; }

      .editEventPreviewBody h2 {
        margin-top: 5px;
        font-size: 22px;
      }

      .editEventPreviewFacts {
        gap: 7px;
        margin-top: 13px;
      }

      .editEventPreviewFacts article {
        padding: 10px;
        border-radius: 12px;
      }

      .editEventPreviewHint {
        margin-top: 9px;
        padding: 11px 12px;
        border-radius: 13px;
      }

      @media (max-width: 900px) {
        .editEventPage {
          padding: 78px 12px 38px;
        }

        .editEventHero {
          min-height: 0;
          padding: 22px 20px;
          border-radius: 23px;
        }

        .editEventHeroCopy {
          padding-top: 4px;
        }

        .editEventHeroCopy h1 {
          font-size: clamp(38px, 10vw, 54px);
        }

        .editEventHeroCopy p {
          margin-top: 13px;
        }

        .editEventHeroStats {
          position: static;
          width: 100%;
          margin-top: 22px;
        }

        .editEventLayout {
          grid-template-columns: 1fr;
        }

        .editEventPreview {
          position: static;
          order: -1;
        }
      }

      @media (max-width: 600px) {
        .editEventPage {
          padding: 72px 8px 28px;
        }

        .editEventHero {
          padding: 18px 16px;
          border-radius: 20px;
        }

        .editEventEyebrow {
          padding: 6px 9px;
        }

        .editEventHeroCopy h1 {
          margin-top: 14px;
          font-size: 38px;
          line-height: .96;
        }

        .editEventHeroCopy p {
          font-size: 11px;
          line-height: 1.55;
        }

        .editEventHeroStats {
          grid-template-columns: repeat(3, minmax(0, 1fr));
          margin-top: 16px;
        }

        .editEventHeroStats article {
          padding: 10px 8px;
        }

        .editEventHeroStats strong { font-size: 12px; }

        .editEventToolbar {
          align-items: flex-start;
          gap: 10px;
          margin: 20px 4px 11px;
        }

        .editEventToolbar h2 {
          font-size: 27px;
        }

        .editEventToolbar p {
          display: none;
        }

        .editEventToolbar > a {
          min-height: 38px;
          padding: 0 11px;
          white-space: nowrap;
        }

        .editEventPanel {
          padding: 14px;
          border-radius: 17px;
        }

        .editEventPanelHeader {
          margin-bottom: 11px;
        }

        .editEventPanelIcon {
          width: 34px;
          height: 34px;
        }

        .editEventPanelHeader h2 {
          font-size: 16px;
        }

        .editEventPanelHeader p {
          margin-top: 3px;
          font-size: 8px;
        }

        .editEventFields {
          grid-template-columns: 1fr;
          gap: 9px;
        }

        .editEventField.full {
          grid-column: auto;
        }

        .editEventField input {
          min-height: 42px;
        }

        .editEventField textarea {
          min-height: 88px;
        }

        .editEventActivityGrid,
        .editIncludedPresetGrid {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .editEventActivityGrid button,
        .editIncludedPresetGrid button {
          min-height: 34px;
          padding: 6px 8px;
          font-size: 8px;
        }

        .editEventPreviewImage {
          aspect-ratio: 1.7 / 1;
        }

        .editEventPreviewFacts {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .editEventSubmitBar {
          gap: 10px;
          padding: 11px;
        }

        .editEventSubmitBar > div {
          display: none;
        }

        .editEventSubmitBar button {
          width: 100%;
          min-height: 46px;
        }
      }

      `}</style>
  );
}
